import { Component, inject, OnDestroy, OnInit, PLATFORM_ID } from '@angular/core';
import { Router } from '@angular/router';
import { LoanType } from '@shared/enum';
import { ApplicationService } from '@shared/service/application.service';
import { Subscription } from 'rxjs';
import { StatefulService } from '@app/service/stateful.service';
import { UserService } from '@workflow/ecp-shared/service/user/user.service';
import { ApplicationApiService } from '@workflow/ecp-workflow/dynamic-app/service/application-api/application-api.service';
import { UserInfo } from '@workflow/ecp-shared/model/userinfo';
import { UserSessionService } from '@shared/service/user-session.service';
import { SharedUiLibraryModule } from '@cudirect/shared-ui-library';
import { SawDust } from '@cudirect/sawdust/sawdust';
import { isPlatformBrowser } from '@angular/common';

@Component({
  selector: 'cu-user-dropdown',
  styleUrls: ['./user-dropdown.component.scss'],
  templateUrl: './user-dropdown.component.html',
  standalone: true,
  imports: [SharedUiLibraryModule]
})
export class UserDropdownComponent implements OnInit, OnDestroy {
  // Inject dependencies using the new inject pattern
  private readonly logger = inject(SawDust);
  private readonly router = inject(Router);
  private readonly userService = inject(UserService);
  public readonly applicationApiService = inject(ApplicationApiService);
  private readonly appService = inject(ApplicationService);
  private readonly statefulService = inject(StatefulService);
  private readonly sessionService = inject(UserSessionService);

  public userMenuControl = `<i class="cu-user-circle bl-padding--16"></i><span class="bl-screen-reader-only">User menu</span>`;

  // TODO: All authentication logic should be hoisted to parent component and passed as inputs.
  public isAuthenticated = false;
  public subscriptions = new Subscription();
  public user: UserInfo;
  public displayId: string;
  protected readonly isBrowser = isPlatformBrowser(inject(PLATFORM_ID));

  ngOnInit(): void {
    const authSub = this.userService.authSub.subscribe((isAuthenticated) => {
      // this.logger.info('isAuthenticated', { isAuthenticated });
      this.isAuthenticated = isAuthenticated;
    });
    const userSub = this.userService.userSub.subscribe((user) => {
      this.user = user;
      this.handleDisplayId();
    });

    this.subscriptions.add(userSub);
    this.subscriptions.add(authSub);
  }

  ngOnDestroy(): void {
    this.subscriptions.unsubscribe();
  }

  public handleDisplayId(): void {
    if (this.user && this.isAuthenticated) {
      // eslint-disable-next-line @typescript-eslint/dot-notation
      if (this.user.isCellPhoneUser) {
        // eslint-disable-next-line @typescript-eslint/dot-notation
        let mobileNumber = this.user['cellPhone'];
        if (mobileNumber.length === 10) {
          mobileNumber = `(${mobileNumber.substring(0, 3)}) ${mobileNumber.substring(3, 6)}-${mobileNumber.substring(6)}`;
        }
        this.displayId = mobileNumber;
      } else {
        this.displayId = this.user.email;
      }
    } else {
      // if (!this.user) {
      //   this.logger.warn('No user found');
      // } else {
      //   this.logger.warn('User is not authenticated');
      // }
      this.displayId = null;
    }
  }

  /** Logout functionality clearing local storage and redirected to home page */
  /** Should not be making a to promise call here, the service should be logging out */
  public logout(): void {
    if (this.isPIDInProgress()) return;

    const isAPICallProgress = this.statefulService.wrap('mem:isAPICallProgress').value as boolean;
    if (!isAPICallProgress) {
      this.proceedToLogout();
    } else {
      this.statefulService.wrap('mem:isLogOutPending').value = true;
    }
    this.appService.invalidateCache();
  }

  public get showAccountlink(): boolean {
    if (!this.user) {
      return false;
    }
    return this.user.registeredWithSSO;
  }

  private proceedToLogout(): void {
    this.sessionService.logOut();
  }

  private isPIDInProgress(): boolean {
    const isIdentity = this.isBrowser ? window.location.pathname.includes('/identity') : false;
    const isPIDCompleted = this.statefulService.wrap('api:oaoMemberInformation/isIdentityVerificationCompleted').value;
    const isPIDAvailable = this.statefulService.wrap('api:oaoMemberInformation/oaoLenderSettings/isPIDQuestionsAvailable').value;
    if (!isPIDCompleted && isPIDAvailable && isIdentity) {
      this.statefulService.wrap('mem:preventPIDVerificationExit').value = true;
      const subscription = this.userService.confrimToLeave$.subscribe((result) => {
        this.statefulService.wrap('mem:preventPIDVerificationExit').value = false;
        if (result) {
          this.statefulService.wrap('api:oaoMemberInformation/isIdentityVerificationCompleted').value = true;
          this.saveAndLogout();
        }
        subscription && subscription.unsubscribe();
      });
      return true;
    }

    return false;
  }

  public saveAndLogout(): void {
    const isAPICallProgress = this.statefulService.wrap('mem:isAPICallProgress').value as boolean;
    if (!isAPICallProgress) {
      const storagePrefix = this.userService.UserInfo.loginType === LoanType.OLA ? 'ola' : 'api';
      this.applicationApiService.updateBundleDataToDDMS(storagePrefix, this.logout.bind(this), this.logout.bind(this));
    } else {
      this.statefulService.wrap('mem:isLogOutPending').value = true;
    }
    this.appService.invalidateCache();
  }

  public goToDashboard(): void {
    this.router.navigateByUrl(`/consumer/dashboard?return=dashboard`);
  }

  // Evaluate if string is greater than maximum character length (default 25)
  // TODO: should this be a shared method?
  public isMaxChar(value: string, maxChar = 25): boolean {
    if (!value) return;
    return value.trim().length > maxChar;
  }
}
